import { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Crosshair,
  Play,
  Plus,
  Square,
  Trash2,
} from "lucide-react";
import type { Animation, AnimationStep, GameObject } from "./types";
import {
  animationDefaults,
  duration,
  easings,
  stepDefaults,
  stepKinds,
} from "./animation";
type Props = {
  object: GameObject;
  locked: boolean;
  /** Id of the animation being previewed on the canvas, if any. */
  previewing: string | null;
  onPatch(values: Partial<GameObject>): void;
  onPreview(animation: Animation | null): void;
};
const key = (name: string) => name.trim().toLocaleLowerCase("ru");
// Steps play top to bottom. Each one has its own duration and easing, and
// starts from where the previous one ended.
export function AnimationsEditor({
  object,
  locked,
  previewing,
  onPatch,
  onPreview,
}: Props) {
  const list = object.animations,
    [open, setOpen] = useState<string | null>(list[0]?.id ?? null),
    current = list.find((a) => a.id === open) ?? list[0];
  const update = (id: string, values: Partial<Animation>) =>
    onPatch({
      animations: list.map((a) => (a.id === id ? { ...a, ...values } : a)),
    });
  const steps = (values: AnimationStep[]) =>
    current && update(current.id, { steps: values });
  const patchStep = (index: number, values: Partial<AnimationStep>) =>
    current &&
    steps(
      current.steps.map((s, i) => (i === index ? { ...s, ...values } : s)),
    );
  function add() {
    let n = list.length + 1;
    while (list.some((a) => key(a.name) === `анимация ${n}`)) n++;
    const a = animationDefaults(`Анимация ${n}`);
    onPatch({ animations: [...list, a] });
    setOpen(a.id);
  }
  const number = (
    label: string,
    value: number,
    change: (v: number) => void,
    extra: { min?: number; max?: number; step?: number } = {},
  ) => (
    <label className="step-number">
      {label}
      <input
        type="number"
        aria-label={label}
        value={Math.round(value * 1000) / 1000}
        step={extra.step ?? 1}
        min={extra.min}
        max={extra.max}
        disabled={locked}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v)) change(v);
        }}
      />
    </label>
  );
  const duplicate = current && list.some(
    (a) => a.id !== current.id && key(a.name) === key(current.name),
  );
  return (
    <div className="scene-animations">
      <div className="logic-section-title">
        <span className="eyebrow">АНИМАЦИИ</span>
        <button
          className="text-button"
          disabled={locked || list.length >= 20}
          onClick={add}
        >
          <Plus size={13} />
          Анимация
        </button>
      </div>
      {list.length > 1 && (
        <select
          aria-label="Выбранная анимация"
          value={current?.id}
          onChange={(e) => setOpen(e.target.value)}
        >
          {list.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      )}
      {current ? (
        <div className="animation-card">
          <div className="state-picker">
            <input
              aria-label="Название анимации"
              aria-invalid={duplicate || !current.name.trim()}
              value={current.name}
              maxLength={60}
              disabled={locked}
              onChange={(e) => update(current.id, { name: e.target.value })}
            />
            <button
              className="icon-button"
              aria-label={
                previewing === current.id
                  ? "Остановить предпросмотр"
                  : "Предпросмотр анимации"
              }
              title="Проиграть на холсте"
              onClick={() =>
                onPreview(previewing === current.id ? null : current)
              }
            >
              {previewing === current.id ? (
                <Square size={13} />
              ) : (
                <Play size={13} />
              )}
            </button>
            <button
              className="icon-button"
              aria-label="Дублировать анимацию"
              disabled={locked || list.length >= 20}
              onClick={() => {
                const copy = {
                  ...structuredClone(current),
                  id: crypto.randomUUID(),
                  name: current.name.slice(0, 50) + " копия",
                  steps: current.steps.map((s) => ({
                    ...s,
                    id: crypto.randomUUID(),
                  })),
                };
                onPatch({ animations: [...list, copy] });
                setOpen(copy.id);
              }}
            >
              <Copy size={13} />
            </button>
            <button
              className="icon-button"
              aria-label={`Удалить анимацию ${current.name}`}
              disabled={locked}
              onClick={() => {
                if (previewing === current.id) onPreview(null);
                onPatch({
                  animations: list.filter((a) => a.id !== current.id),
                });
              }}
            >
              <Trash2 size={13} />
            </button>
          </div>
          <label className="game-check">
            <input
              type="checkbox"
              checked={current.loop}
              disabled={locked}
              onChange={(e) => update(current.id, { loop: e.target.checked })}
            />
            Повторять по кругу · всего {Math.round(duration(current) * 100) / 100} с
          </label>
          {duplicate && (
            <p className="small-text error-message" role="alert">
              Названия анимаций должны быть разными.
            </p>
          )}
          <ol className="animation-steps">
            {current.steps.map((s, i) => (
              <li key={s.id} className="animation-step">
                <div className="state-picker">
                  <span className="step-index">{i + 1}</span>
                  <select
                    aria-label={`Шаг ${i + 1}`}
                    value={s.kind}
                    disabled={locked}
                    onChange={(e) => {
                      const kind = e.target.value as AnimationStep["kind"],
                        fresh = stepDefaults(kind);
                      patchStep(i, {
                        kind,
                        x: kind === "move" ? object.x : fresh.x,
                        y: kind === "move" ? object.y : fresh.y,
                        value: fresh.value,
                        state:
                          kind === "state" ? (object.states[0]?.name ?? "") : "",
                      });
                    }}
                  >
                    {Object.entries(stepKinds).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="icon-button"
                    aria-label={`Поднять шаг ${i + 1}`}
                    disabled={locked || i === 0}
                    onClick={() => {
                      const next = [...current.steps];
                      [next[i - 1], next[i]] = [next[i], next[i - 1]];
                      steps(next);
                    }}
                  >
                    <ChevronUp size={12} />
                  </button>
                  <button
                    className="icon-button"
                    aria-label={`Опустить шаг ${i + 1}`}
                    disabled={locked || i === current.steps.length - 1}
                    onClick={() => {
                      const next = [...current.steps];
                      [next[i + 1], next[i]] = [next[i], next[i + 1]];
                      steps(next);
                    }}
                  >
                    <ChevronDown size={12} />
                  </button>
                  <button
                    className="icon-button"
                    aria-label={`Удалить шаг ${i + 1}`}
                    disabled={locked}
                    onClick={() =>
                      steps(current.steps.filter((_, j) => j !== i))
                    }
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                <div className="step-fields">
                  {number(
                    "Секунд",
                    s.duration,
                    (duration) =>
                      patchStep(i, {
                        duration: Math.max(0, Math.min(60, duration)),
                      }),
                    { min: 0, max: 60, step: 0.05 },
                  )}
                  {!["state", "wait"].includes(s.kind) && (
                    <label className="step-number">
                      Ход
                      <select
                        aria-label="Плавность"
                        value={s.easing}
                        disabled={locked}
                        onChange={(e) =>
                          patchStep(i, {
                            easing: e.target.value as AnimationStep["easing"],
                          })
                        }
                      >
                        {Object.entries(easings).map(([k, label]) => (
                          <option key={k} value={k}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {(s.kind === "move" || s.kind === "offset") && (
                    <>
                      {number(s.kind === "move" ? "X" : "ΔX", s.x, (x) =>
                        patchStep(i, { x }),
                      )}
                      {number(s.kind === "move" ? "Y" : "ΔY", s.y, (y) =>
                        patchStep(i, { y }),
                      )}
                    </>
                  )}
                  {s.kind === "move" && (
                    <button
                      className="icon-button step-pick"
                      aria-label="Взять текущее положение объекта"
                      title="Перетащи объект в нужную точку и нажми"
                      disabled={locked}
                      onClick={() => patchStep(i, { x: object.x, y: object.y })}
                    >
                      <Crosshair size={13} />
                    </button>
                  )}
                  {s.kind === "rotate" &&
                    number("Градусов", s.value, (value) =>
                      patchStep(i, { value }),
                    )}
                  {s.kind === "opacity" &&
                    number(
                      "Непрозрачность, %",
                      s.value * 100,
                      (v) =>
                        patchStep(i, {
                          value: Math.max(0, Math.min(100, v)) / 100,
                        }),
                      { min: 0, max: 100, step: 5 },
                    )}
                  {s.kind === "shadow" &&
                    number(
                      "Размытие, px",
                      s.value,
                      (v) =>
                        patchStep(i, { value: Math.max(0, Math.min(100, v)) }),
                      { min: 0, max: 100 },
                    )}
                  {s.kind === "state" && (
                    <label className="step-number">
                      Картинка
                      <select
                        aria-label="Состояние шага"
                        value={s.state}
                        disabled={locked}
                        onChange={(e) => patchStep(i, { state: e.target.value })}
                      >
                        <option value="">Исходная</option>
                        {object.states.map((st) => (
                          <option key={st.id} value={st.name}>
                            {st.name}
                          </option>
                        ))}
                        {s.state &&
                          !object.states.some(
                            (st) => key(st.name) === key(s.state),
                          ) && <option value={s.state}>{s.state} (нет)</option>}
                      </select>
                    </label>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <button
            className="text-button"
            disabled={locked || current.steps.length >= 50}
            onClick={() => steps([...current.steps, stepDefaults()])}
          >
            <Plus size={13} />
            Шаг
          </button>
        </div>
      ) : (
        <p className="small-text muted">
          Цепочка шагов: сдвиг, поворот, прозрачность, тень, смена картинки.
          Запускается правилом «Проиграть анимацию».
        </p>
      )}
    </div>
  );
}
